import { useState, type ReactNode } from 'react'
import {
  Banner,
  Button,
  Chip,
  Field,
  Modal,
  MoneyInput,
  PresetAmounts,
  Segmented,
  useToast,
} from '@w7/ui'
import { convertDisplayAmount, parseCurrencyType } from '@/lib/currency'
import { useTransferCredit } from '@/app/queries'
import { round2 } from '@/lib/book'
import { money } from '@/lib/format'
import { ROLE_LABEL, type Account } from '@/data/types'
import { capFor, zeroingMove } from './settle'
import './downline.css'

/**
 * Moving credit, as a popup.
 *
 * Compact Give/Take: direction, amount (+ cap/presets), submit. No before/after
 * balance strip and no remark (Lab Give/Take modal has neither).
 *
 * Exported so `MemberPage`'s `CreditDrawer` can adopt it in a later change.
 */

/** Round down to a tidy figure, so a preset never lands a rupee over a cap. */
function roundDown(value: number): number {
  if (!(value > 0)) return 0
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  return Math.floor(value / magnitude) * magnitude
}

function presetsFor(creditLimit: number, cap: number): number[] {
  const candidates = [0.1, 0.25, 0.5, 1].map((fraction) => roundDown(creditLimit * fraction))
  return [...new Set([...candidates, roundDown(cap)])]
    .filter((amount) => amount > 0 && amount <= cap)
    .sort((a, b) => a - b)
}

/**
 * Bold one figure inside a sentence that is owned elsewhere.
 *
 * The cap sentence lives in `capFor` because the plan, the run and the report
 * quote the same wording; re-typing it here with a `<strong>` in the middle is
 * exactly how the dialog and the plan start explaining the same cap two ways.
 */
function emphasise(text: string, needle: string): ReactNode {
  const at = text.indexOf(needle)
  if (at === -1) return text
  return (
    <>
      {text.slice(0, at)}
      <strong>{needle}</strong>
      {text.slice(at + needle.length)}
    </>
  )
}

export function TransferDialog({
  self,
  account,
  initialDirection,
  initialAmount,
  onClose,
}: {
  self: Account
  account: Account
  initialDirection: 'give' | 'take'
  /** Click-to-zero prefill. Undefined when opened from the Give/Take buttons. */
  initialAmount?: number
  onClose: () => void
}) {
  const zeroing = zeroingMove(account)
  // Settlement lock: outstanding Take → only Take; outstanding Give → only Give.
  const [direction, setDirection] = useState<'give' | 'take'>(
    () => zeroing?.direction ?? initialDirection,
  )
  const [raw, setRaw] = useState(initialAmount ? String(initialAmount) : '')
  // Tracks whether what is in the box is OURS or the operator's. Flipping the
  // toggle throws away a prefill; it must never throw away a typed figure.
  const [prefilled, setPrefilled] = useState(initialAmount !== undefined)
  const [submitted, setSubmitted] = useState(false)
  /* One key per opening of this dialog, reused by every attempt made from it: a
     retry after a failure must not be able to move the money twice. */
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const transfer = useTransferCredit()
  const toast = useToast()

  const activeDirection = zeroing?.direction ?? direction
  const giving = activeDirection === 'give'
  const cleaned = raw.replace(/[^\d.]/g, '')
  const parsed = cleaned === '' ? 0 : Number(cleaned)
  const amount = Number.isFinite(parsed) ? round2(parsed) : 0

  const capInfo = capFor(self, account, activeDirection)
  const { freeToGive } = capInfo
  const childCcy = parseCurrencyType(account.currencyType)
  const selfCcy = parseCurrencyType(self.currencyType)
  const fmtChild = (n: number, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: childCcy, alreadyDisplay: true })
  const fmtSelf = (n: number, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: selfCcy, alreadyDisplay: true })
  // Give cap is parent's free rupees/pts; the box is in the child's currency.
  const cap =
    activeDirection === 'give'
      ? convertDisplayAmount(capInfo.cap, selfCcy, childCcy)
      : capInfo.cap
  const amountInSelf = convertDisplayAmount(amount, childCcy, selfCcy)

  const directionItems =
    zeroing?.direction === 'take'
      ? ([{ id: 'take' as const, label: 'Take from them' }] as const)
      : zeroing?.direction === 'give'
        ? ([{ id: 'give' as const, label: 'Give to them' }] as const)
        : ([
            { id: 'give' as const, label: 'Give to them' },
            { id: 'take' as const, label: 'Take from them' },
          ] as const)

  // Outstanding Give/Take = Lab settlement (no TB required). Cash-only Give
  // still uses free-to-give; cash-only Take still uses child balance.
  const settlementOutstanding = zeroing != null && zeroing.direction === activeDirection
  const problem = !(amount > 0)
    ? 'Enter an amount greater than zero.'
    : giving && amount > cap
      ? settlementOutstanding
        ? `Amount should be less than or equal to the Give amount (${fmtChild(cap)}).`
        : amountInSelf > freeToGive
          ? `You have ${fmtSelf(freeToGive)} available — your ${fmtSelf(self.balance)} balance less the ${fmtSelf(self.exposure)} already committed to open bets.`
          : `Amount should be less than or equal to the Give amount (${fmtChild(cap)}).`
      : !giving && amount > cap
        ? settlementOutstanding
          ? `Amount should be less than or equal to the Take amount (${fmtChild(cap)}).`
          : amount > account.balance
            ? account.balance > 0
              ? `${account.username} holds ${fmtChild(account.balance)}. You cannot take more than they hold.`
              : `${account.username} holds ${fmtChild(account.balance)}, so there is nothing to take.`
            : `Amount should be less than or equal to the Take amount (${fmtChild(cap)}).`
        : null

  // "Enter an amount" is not an error until they try to submit; a breached cap
  // is worth saying the MOMENT it is typed — including on the very first render
  // when the click-to-zero prefill already exceeds it.
  const showProblem = problem !== null && (submitted || amount > 0)
  const overCap = amount > 0 && amount > cap && cap > 0

  const setAmount = (next: number, fromPreset: boolean) => {
    setRaw(String(next))
    setPrefilled(fromPreset)
    if (transfer.isError) transfer.reset()
  }

  const switchDirection = (next: 'give' | 'take') => {
    if (zeroing && next !== zeroing.direction) return
    setDirection(next)
    setSubmitted(false)
    transfer.reset()
    if (prefilled) {
      setRaw('')
      setPrefilled(false)
    }
  }

  const submit = () => {
    setSubmitted(true)
    if (problem) return
    transfer.mutate(
      {
        accountId: account.id,
        amount,
        direction: activeDirection,
        remark: '',
        idempotencyKey,
        currencyType: account.currencyType,
      },
      {
        onSuccess: (result) => {
          // Every figure below is quoted from the ledger's reply, never from
          // what was predicted here; the tables refresh off the invalidation.
          const settled = round2(result.settlementAfter) === 0
          toast.success(
            `${fmtChild(amount)} ${giving ? 'given to' : 'taken from'} ${account.username}`,
            `You now hold ${fmtSelf(result.from.id === self.id ? result.from.balance : result.to.balance)} · ${account.username} now holds ${fmtChild(result.from.id === account.id ? result.from.balance : result.to.balance)} · ${settled ? 'settled to zero' : `${fmtChild(Math.abs(result.settlementAfter))} still outstanding`} · ref ${result.transfer.ref}`,
          )
          onClose()
        },
      },
    )
  }

  const outstanding =
    account.settlement === 0
      ? ''
      : account.settlement > 0
        ? ` · ${fmtChild(account.settlement)} outstanding in your favour`
        : ` · ${fmtChild(-account.settlement)} outstanding to them`

  return (
    <Modal
      open
      onClose={onClose}
      title={giving ? `Give credit to ${account.username}` : `Take credit from ${account.username}`}
      subtitle={`${ROLE_LABEL[account.role]} · ${account.displayName}${outstanding}`}
      footer={
        <>
          <span className="w7-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={transfer.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={giving ? 'give' : 'take'}
            busy={transfer.isPending}
            disabled={problem !== null}
            onClick={submit}
          >
            {amount > 0
              ? `${giving ? 'Give' : 'Take'} ${fmtChild(amount)}`
              : giving
                ? 'Give credit'
                : 'Take credit'}
          </Button>
        </>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
        <Segmented
          label="Direction of the movement"
          value={activeDirection}
          onChange={switchDirection}
          items={[...directionItems]}
        />

        <Field
          label="Amount"
          required
          aside={
            cap > 0 ? (
              // FLOORED, never rounded. A chip reading "₹6,786" against a ledger
              // that accepts ₹6,785.56 is a papercut you only discover at submit.
              <Chip size="sm" tone="muted">
                Max {fmtChild(Math.floor(cap), { round: true })}
              </Chip>
            ) : null
          }
          hint={emphasise(capInfo.because, fmtChild(cap))}
          error={showProblem ? problem : null}
        >
          {({ id, describedBy }) => (
            <MoneyInput
              id={id}
              aria-describedby={describedBy}
              invalid={showProblem}
              currency={childCcy}
              value={raw}
              autoComplete="off"
              onChange={(event) => {
                setRaw(event.target.value)
                setPrefilled(false)
                if (transfer.isError) transfer.reset()
              }}
            />
          )}
        </Field>

        {/* The one-tap way out of an over-cap prefill. Shrinking the prefill
            silently would hide the single most important fact on the row: that
            some of it is going to stay outstanding. */}
        {overCap ? (
          <div className="w7-row-wrap" style={{ gap: 6 }}>
            <button
              type="button"
              className="w7-filterchip"
              aria-label={`Use the maximum you can move now, ${fmtChild(cap)} — ${fmtChild(round2(amount - cap))} would stay outstanding.`}
              onClick={() => setAmount(cap, true)}
            >
              Settle what you can — {fmtChild(cap)}
            </button>
          </div>
        ) : null}

        {/* Cap of exactly nothing. `overCap` above cannot cover this — it is
            gated on `cap > 0`, because "settle what you can" has nothing to
            offer when what you can is zero. Without this branch the dialog sits
            in its worst state: an amount prefilled, an error explaining it
            cannot move, a settle-to-zero preset that only re-enters the same
            error, and no sentence telling the operator what happens instead.
            The wording is the bulk report's, deliberately — `settle.ts` already
            argues that one cap explained two ways is a support ticket, and this
            is the same cap on the same row, reached through the other door. */}
        {zeroing && zeroing.direction === direction && cap <= 0 ? (
          <Banner tone="warn" title="This cannot be settled right now">
            {capInfo.because} {fmtChild(zeroing.amount)} stays outstanding
            {giving ? '' : ` against ${account.username}`}. Settle when they have funded, or move
            the other way.
          </Banner>
        ) : null}

        <div className="w7-row-wrap" style={{ gap: 6 }}>
          {/* Settle-to-zero comes FIRST and reads as the recommended choice. It
              disappears when the toggle is flipped away from its direction — a
              "settle to zero" that moves the settlement further FROM zero is
              meaningless — and when the cap is zero, where it would be an action
              that cannot succeed. Over-cap-but-possible still shows it: there
              the operator has "settle what you can" to fall back on. */}
          {zeroing && zeroing.direction === direction && cap > 0 ? (
            <button
              type="button"
              className="w7-filterchip w7-settle-preset"
              aria-label={`Settle to zero, ${fmtChild(zeroing.amount)}`}
              title={
                zeroing.amount > cap
                  ? `More than you can move right now — picking it will show what the cap is and what would stay outstanding.`
                  : undefined
              }
              onClick={() => setAmount(zeroing.amount, true)}
            >
              Settle to zero {fmtChild(zeroing.amount, { round: true })}
            </button>
          ) : null}
          <PresetAmounts
            presets={presetsFor(account.creditLimit, cap)}
            format={(preset) => fmtChild(preset, { round: true })}
            onPick={(preset) => setAmount(preset, true)}
          />
        </div>

        {transfer.isError ? (
          <Banner tone="error" title="The credit did not move">
            {(transfer.error as Error)?.message} Your amount is still entered — fix the problem and
            try again, or close this and nothing has happened.
          </Banner>
        ) : null}
      </div>
    </Modal>
  )
}
